import { get, post } from 'axios';
import { observable, action, runInAction } from 'mobx';
import { LAB_COMPATIBILITY_API } from 'config/constants';
import {
  type ClientTestResult,
  getUserRegion,
  executeClientTests,
} from './clientTests';

export default class ConnectivityTesterStore {
  @observable running: boolean = false;

  @observable finished: boolean = false;

  @observable failed: boolean = false;

  @observable invokeIframe: boolean = false;

  @observable docId: string;

  @observable progress: number = 0;

  userStore: any;

  @observable testResult = {
    browser: {},
    summary: {},
    tests: [],
  };

  base_url: string;

  startTime: Date;

  INITIAL_PROGRESS: number = 5;

  IFRAME_PROGRESS = 50;

  CLIENT_PROGRESS = 45;

  // Timeout logic
  @observable iframeTimeoutId: NodeJS.Timeout | null = null;

  IFRAME_TIMEOUT_MS = 6 * 60 * 1000; // 6 minutes

  CLIENT_TIMEOUT_MS = 60 * 1000; // 1 minute

  visualProgressIntervalId: NodeJS.Timeout | null = null;

  clientTestsStarted: boolean = false;

  configStore: any;

  constructor(userStore, configStore) {
    this.configStore = configStore;
    this.userStore = userStore;
  }

  handleFailure(message: string) {
    console.error('An error occurred:', message);
    this.running = false;
    this.failed = true;
    this.invokeIframe = false;
    this.stopVisualProgress();
  }

  private async runClientTestsWithTimeout(): Promise<ClientTestResult[]> {
    const clientTestsPromise = executeClientTests(this.base_url);
    const timeoutPromise = new Promise<never>((_, reject) =>
      setTimeout(
        () => reject(new Error('CLIENT_TESTS_TIMEOUT')),
        this.CLIENT_TIMEOUT_MS,
      ),
    );

    try {
      return await Promise.race([clientTestsPromise, timeoutPromise]);
    } catch {
      return [
        {
          id: 'client-timeout',
          name: 'Client Tests',
          status: 'error',
          details: `Client-side tests timed out after ${
            this.CLIENT_TIMEOUT_MS / 1000
          } seconds. Please check your internet connection.`,
          duration: this.CLIENT_TIMEOUT_MS,
          timestamp: new Date().toISOString(),
        },
      ];
    }
  }

  /**
   * Helper function to build the final combined payload and POST it
   */
  async submitCombinedResults(bbbTests: any[]) {
    try {
      this.progress = 50; // Bump progress visually while client tests run

      this.startVisualProgress(
        this.IFRAME_PROGRESS,
        this.IFRAME_PROGRESS + this.CLIENT_PROGRESS, // increase up to 95%
        this.CLIENT_TIMEOUT_MS,
      );

      // 1. Run the client-side tests safely
      const clientTests = await this.runClientTestsWithTimeout();

      // 2. Combine the arrays
      const combinedTests = [...bbbTests, ...clientTests];

      // 3. Calculate the overall summary
      const failed = combinedTests.filter((r) => r.status === 'error').length;
      const successful = combinedTests.length - failed;

      const payload = {
        browser: {
          name: navigator.appName,
          version: navigator.appVersion,
          userAgent: navigator.userAgent,
          platform: navigator.platform,
        },
        summary: {
          total: combinedTests.length,
          successful,
          failed,
          timestamp: new Date().toISOString(),
        },
        tests: combinedTests,
      };

      // 4. Send the single POST request!
      const updateUrl = `${LAB_COMPATIBILITY_API}${this.docId}/@@update_test_results`;
      await post(updateUrl, payload);

      // 5. Fetch final state to render
      await this.fetchData();
    } catch (error) {
      this.handleFailure('Failed to submit combined test results.');
    }
  }

  @action handleIframeTimeout = async () => {
    if (this.clientTestsStarted) return;

    console.warn('BBB Iframe tests timed out.');
    this.invokeIframe = false;
    this.clientTestsStarted = true;

    // Dummy payload indicating the timeout failure
    const timeoutPayload = [
      {
        id: 'bbb-timeout',
        name: 'WebRTC Connection',
        status: 'error',
        details:
          'The external WebRTC compatibility tests timed out and did not complete. Please check if your network blocks WebRTC or UDP traffic.',
        duration: this.IFRAME_TIMEOUT_MS,
        timestamp: new Date().toISOString(),
      },
    ];

    await this.submitCombinedResults(timeoutPayload);
  };

  @action processIframeResults = async (bbbPayload) => {
    if (this.clientTestsStarted) return;

    if (this.iframeTimeoutId) {
      clearTimeout(this.iframeTimeoutId);
      this.iframeTimeoutId = null;
    }

    this.clientTestsStarted = true;
    this.invokeIframe = false; // We can hide the iframe now

    const bbbTests = bbbPayload.tests || [];
    await this.submitCombinedResults(bbbTests);
  };

  @action fetchData = async () => {
    try {
      const { data } = await get(`${LAB_COMPATIBILITY_API}${this.docId}`);
      if (data) {
        this.completeTest(data);
      }
    } catch (error) {
      console.error('An error occurred:', error);
    }
  };

  completeTest(data) {
    this.stopVisualProgress();
    this.progress = 100;
    this.testResult = data;

    setTimeout(() => {
      runInAction(() => {
        this.running = false;
        this.finished = true;
      });
    }, 1000); // give a second for the user to see it reached 100%
  }

  @action startVisualProgress = (
    startPercent: number,
    targetPercent: number,
    durationMs: number,
  ) => {
    this.stopVisualProgress();

    this.progress = startPercent;

    const phaseStartTime = new Date().valueOf();

    // Update the progress bar every 1 second (1000ms)
    this.visualProgressIntervalId = setInterval(() => {
      const now = new Date();
      const elapsed = now.valueOf() - phaseStartTime;

      const totalIncrease = targetPercent - startPercent;
      const progressIncrement = (elapsed / durationMs) * totalIncrease;

      runInAction(() => {
        // Cap it at 50% max during this phase
        this.progress = Math.min(
          targetPercent,
          startPercent + progressIncrement,
        );
      });
    }, 1000);
  };

  @action stopVisualProgress = () => {
    if (this.visualProgressIntervalId) {
      clearInterval(this.visualProgressIntervalId);
      this.visualProgressIntervalId = null;
    }
  };

  @action runTests = async () => {
    this.startTime = new Date();

    const browserCountry = navigator?.language?.split?.('-')?.[1];
    const region = await getUserRegion(
      this.userStore.defaultCountry || browserCountry || 'US',
    );
    const hosts = this.configStore.networkTestHosts;
    const host = hosts[region.toLowerCase()] || hosts.northamerica;
    this.base_url = `https://${host}/images/`;

    try {
      const response = await post(LAB_COMPATIBILITY_API, {
        username: this.userStore.username,
      });
      if (response.data) {
        this.docId = response.data.doc_id;
        this.running = true;
        this.progress = this.INITIAL_PROGRESS;
        this.clientTestsStarted = false;

        // This will render the Iframe in index.tsx
        this.invokeIframe = true;
        this.startVisualProgress(
          this.INITIAL_PROGRESS,
          this.IFRAME_PROGRESS,
          this.IFRAME_TIMEOUT_MS,
        );

        // START THE FAILSAFE TIMEOUT
        this.iframeTimeoutId = setTimeout(() => {
          this.handleIframeTimeout();
        }, this.IFRAME_TIMEOUT_MS);
      }
    } catch (error) {
      this.handleFailure('Failed to initialize tests.');
    }
  };

  @action getLastTestResults = async () => {
    const url = `${window.location.origin}${LAB_COMPATIBILITY_API}@@get_last_test_results`;
    try {
      const response = await post(url);
      if (response.data) {
        const item = response.data;
        if (item.tests) {
          this.finished = true;
          this.testResult.browser = item.browser;
          this.testResult.summary = item.summary;
          this.testResult.tests = item.tests;
        }
      }
    } catch (error) {
      console.log(error); // eslint-disable-line no-console
    }
  };
}
